import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "@/components/Icon";
import { Menu } from "@/components/Menu";
import { NavigationLinks } from "@/components/workspace/NavigationLinks";
import { FavoriteNavigation } from "@/components/workspace/FavoriteNavigation";
import type { NavigationArea } from "@/components/workspace/navigation";

interface TopNavigationProps {
    areas: NavigationArea[];
    activeAreaId?: string;
    favorites: string[];
    onFavorite: (id: string) => void;
    onReorderFavorite: (sourceId: string, targetId: string) => void;
    showAreas: boolean;
    exploring: boolean;
}

export const TopNavigation = ({
    areas,
    activeAreaId,
    favorites,
    onFavorite,
    onReorderFavorite,
    showAreas,
    exploring,
}: TopNavigationProps) => {
    const { t } = useTranslation("global");
    const host = useRef<HTMLDivElement>(null);
    const [count, setCount] = useState(0);
    const [favoriteShake, setFavoriteShake] = useState(false);
    const areaOrder = [
        "header_settings",
        "header_admin",
        "contracting",
        "header_tenders",
        "purchases",
    ];
    const displayAreas = [...areas].sort((a, b) => {
        const left = areaOrder.indexOf(a.id);
        const right = areaOrder.indexOf(b.id);
        return (left < 0 ? areaOrder.length : left) -
            (right < 0 ? areaOrder.length : right);
    });
    const signature = displayAreas
        .map((area) => `${area.id}:${area.label}`)
        .join("|");
    useEffect(() => {
        const handleFavoriteAdded = (): void => {
            window.setTimeout(() => {
                setFavoriteShake(true);
                window.setTimeout(() => setFavoriteShake(false), 520);
            }, 520);
        };
        window.addEventListener("favorite-added", handleFavoriteAdded);
        return () =>
            window.removeEventListener("favorite-added", handleFavoriteAdded);
    }, []);
    useLayoutEffect(() => {
        const element = host.current;
        if (!element) {
            return;
        }

        const measure = (): void => {
            const widths = Array.from(
                element.querySelectorAll<HTMLElement>("[data-area-measure]")
            ).map((child) => child.getBoundingClientRect().width);
            const more =
                element
                    .querySelector<HTMLElement>("[data-more-measure]")
                    ?.getBoundingClientRect().width || 60;
            const space = element.clientWidth;
            let used = more;
            let visible = 0;

            for (const width of widths) {
                if (used + width + 4 > space) {
                    break;
                }

                used += width + 4;
                visible++;
            }

            setCount(visible);
        };

        const observer = new ResizeObserver(measure);
        observer.observe(element);
        measure();

        void document.fonts.ready.then(() => {
            if (element.isConnected) {
                measure();
            }
        });

        return () => observer.disconnect();
    }, [signature, showAreas, t]);
    const label = (
        name: string,
        icon?: string,
        showChevron = true
    ): React.ReactNode => (
        <span className="inline-flex h-9 items-center gap-1.5 whitespace-nowrap px-2.5 text-xs font-medium transition-colors hover:font-semibold hover:text-primary dark:hover:text-primary-300">
            {icon && <Icon value={icon} size={16} />}
            <span>{name}</span>
            {showChevron && <Icon value="chevronDown" size={14} />}
        </span>
    );
    const menuLabel = (
        <span className="inline-flex h-9 items-center gap-1.5 whitespace-nowrap px-2.5 text-xs font-medium transition-colors hover:font-semibold hover:text-primary dark:hover:text-primary-300">
            <Icon value="menu" size={16} />
            <span>{t("workspace.menu")}</span>
        </span>
    );
    const links = (
        area: NavigationArea,
        close: () => void
    ): React.ReactNode => (
        <section key={area.id}>
            <p className="px-3 py-2 text-xs font-semibold text-slate-500 dark:text-zinc-400">
                {area.label}
            </p>
            <NavigationLinks
                items={area.items}
                favorites={favorites}
                onFavorite={onFavorite}
                onNavigate={close}
            />
        </section>
    );
    return (
        <div
            className={`flex min-w-0 items-center justify-start gap-1 ${showAreas ? "lg:flex-1" : ""}`}
        >
            <div
                ref={host}
                className={`relative flex min-w-16 items-center justify-start gap-1 ${showAreas ? "lg:flex-1" : ""}`}
            >
                <div
                    aria-hidden="true"
                    className="pointer-events-none invisible absolute inset-0 overflow-hidden"
                >
                    <div className="flex w-max">
                        {displayAreas.map((area) => (
                            <span
                                key={area.id}
                                data-area-measure
                                className="border border-transparent"
                            >
                                {label(area.label)}
                            </span>
                        ))}
                        <span
                            data-more-measure
                            className="border border-transparent"
                        >
                            {menuLabel}
                        </span>
                    </div>
                </div>
                {showAreas && (
                    <div className="flex min-w-0 items-center justify-center gap-1">
                        {displayAreas.slice(0, count).map((area) => (
                            <Menu
                                key={area.id}
                                label={area.label}
                                trigger={label(area.label)}
                                align="left"
                                active={activeAreaId === area.id && !exploring}
                            >
                                {(close) => links(area, close)}
                            </Menu>
                        ))}
                    </div>
                )}
                <Link
                    to="/navigation"
                    aria-label={t("workspace.menu")}
                    aria-current={exploring ? "page" : undefined}
                    className={`btn btn-sm ghost !border-transparent !shadow-none shrink-0 !p-0 !text-xs transition-colors hover:!font-semibold hover:!text-primary dark:text-zinc-300 dark:hover:bg-white/5 dark:hover:!text-primary-300 ${exploring ? "!bg-primary/10 !text-primary dark:!text-primary-300" : "!text-slate-700 dark:!text-zinc-200"}`}
                >
                    {menuLabel}
                </Link>
            </div>
            <Menu
                label={t("menu.favorites.header")}
                align="left"
                trigger={
                    <div
                        data-favorites-trigger="true"
                        className={`inline-flex h-9 items-center gap-1.5 px-2.5 ${favoriteShake ? "favorite-trigger-shake" : ""}`}
                    >
                        <Icon
                            value="heart"
                            size={16}
                            className="fill-primary text-primary"
                        />
                        <div className="hidden whitespace-nowrap text-xs xl:block">
                            {t("menu.favorites.header")}
                        </div>
                        <div className="hidden xl:inline-flex">
                            <Icon value="chevronDown" size={14} />
                        </div>
                    </div>
                }
            >
                {(close) => (
                    <FavoriteNavigation
                        areas={displayAreas}
                        favorites={favorites}
                        onFavorite={onFavorite}
                        onReorderFavorite={onReorderFavorite}
                        onNavigate={close}
                    />
                )}
            </Menu>
        </div>
    );
};
